/**
 * StepUpService — BFF ceremony driver (ADR-0035 §Edge-bff ceremony).
 *
 * The SPA NEVER receives or stores tokens. The BFF drives the Keycloak OIDC re-auth
 * and swaps fresher tokens into the SAME Redis session. The SESSION cookie is unchanged.
 *
 * Flow:
 *   1. POST /bff/step-up/begin   {challengeId}
 *      → Returns { redirectUri, callbackChannel, expiresInSeconds }.
 *   2. The SPA navigates a popup to redirectUri. Keycloak completes → BFF callback swaps tokens
 *      into the existing Redis session and a BFF return page signals the exact opener/channel.
 *   3. POST /bff/step-up/complete
 *      → Returns { stepUpNonce? } — present iff singleUse=true.
 *   4. Caller retries the original action carrying the nonce (if applicable).
 */
import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { StepUpChallenge } from './step-up.model';

/** Response from POST /bff/step-up/begin. */
export interface StepUpBeginResponse {
  /**
   * A server-bound adapter may finish its explicit ceremony out of band. The ordinary OIDC
   * response omits this field and always uses the validated popup callback below.
   */
  completionMode?: 'POPUP_CALLBACK' | 'OUT_OF_BAND';
  /**
   * BFF authorization URI or the exact dedicated Identity ceremony origin. Completion is always same-origin.
   */
  redirectUri: string;
  /** Opaque, short-lived, same-session popup callback channel. */
  callbackChannel: string;
  /** Server-clamped lifetime for the pending popup handshake. */
  expiresInSeconds: number;
}

/** Response from POST /bff/step-up/complete. */
export interface StepUpCompleteResponse {
  /** Server-authoritative completion verdict. */
  satisfied: boolean;
  /**
   * Single-use nonce bound to the actionFingerprint.
   * Present iff singleUse=true and the ceremony satisfied all requirements.
   * The caller carries this on the retried request; the entitlements walker
   * validates it server-side.
   */
  stepUpNonce: string | null;
}

/**
 * Which completion lane a {@link StepUpService} implementation drives.
 *
 * `POPUP_CALLBACK` finishes in a same-origin popup that must be opened synchronously inside the
 * user gesture; `OUT_OF_BAND` finishes elsewhere (e.g. an operation-bound device prompt) and must
 * NOT have a popup opened for it. Declared synchronously because `window.open` is only honoured
 * inside the gesture task — the dialog cannot wait for `begin()` to tell it.
 */
export type StepUpCompletionLane = 'POPUP_CALLBACK' | 'OUT_OF_BAND';

@Injectable({ providedIn: 'root' })
export class StepUpService {
  private readonly http = inject(HttpClient);

  /**
   * The OIDC ceremony always completes through the validated same-origin popup callback.
   * An adapter that completes out of band overrides this (see the debug build's
   * `DebugStepUpService`).
   */
  readonly completionLane: StepUpCompletionLane = 'POPUP_CALLBACK';

  /**
   * POST /bff/step-up/begin {challengeId}
   *
   * Registers the pending popup handshake. The caller must navigate its already-open popup to
   * redirectUri and wait for the exact return signal before calling complete.
   */
  begin(challenge: StepUpChallenge): Observable<StepUpBeginResponse> {
    return this.http.post<StepUpBeginResponse>(challenge.reauth.begin, {
      challengeId: challenge.challengeId,
    });
  }

  /**
   * POST /bff/step-up/complete
   *
   * Called after the Keycloak re-auth has completed and the BFF has swapped
   * the fresh tokens into the Redis session. Returns the per-action nonce if
   * singleUse=true, null otherwise.
   */
  complete(challenge: StepUpChallenge): Observable<StepUpCompleteResponse> {
    return this.http.post<StepUpCompleteResponse>(challenge.reauth.complete, {
      challengeId: challenge.challengeId,
    });
  }
}

/** Retain the real driver when a debug-build adapter is served without server debug-host evidence. */
export const TB_OIDC_STEP_UP_SERVICE = new InjectionToken<StepUpService>('TB_OIDC_STEP_UP_SERVICE', {
  providedIn: 'root', factory: () => new StepUpService(),
});
